import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import baseUrl from "../../../../api/baseUrl";

type AbsentRow = {
  dentistName: string;
  day: string;
  sessionName: string;
  startTime: string;
  endTime: string;
  roomNo: string;
  doctorId?: string;
  roomId?: string;
};

type DentistOption = { _id: string; name: string };
type DentistGeneralSession = {
  sessionName: string;
  startTime: string;
  endTime: string;
  roomNo: string;
  roomId: string;
};

function SmartDropdown<T extends { key: string; label: string; metaRight?: string }>({
  id,
  labelId,
  valueKey,
  options,
  loading,
  placeholder,
  required,
  disabled,
  onSelect,
}: {
  id: string;
  labelId: string;
  valueKey: string;
  options: T[];
  loading?: boolean;
  placeholder: string;
  required?: boolean;
  disabled?: boolean;
  onSelect: (opt: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => options.find((o) => o.key === valueKey) ?? null,
    [options, valueKey]
  );

  return (
    <div className="relative">
      <button
        id={id}
        type="button"
        aria-labelledby={labelId}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        className="w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-left flex items-center justify-between gap-2 text-sm font-medium text-slate-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-50"
      >
        <div className="min-w-0 flex items-center justify-between gap-2 w-full">
          <span className={selected ? "truncate text-slate-800" : "truncate text-slate-400"}>
            {loading ? "Loading…" : selected?.label ?? placeholder}
          </span>
          {!loading && selected?.metaRight ? (
            <span className="shrink-0 rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600 tabular-nums">
              {selected.metaRight}
            </span>
          ) : null}
        </div>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4 text-slate-500 shrink-0"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {required ? (
        <input
          tabIndex={-1}
          value={selected?.label ?? ""}
          onChange={() => {}}
          required
          className="pointer-events-none absolute opacity-0 h-0 w-0"
        />
      ) : null}

      {open ? (
        <div
          className="absolute z-[70] mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-56 overflow-auto"
          role="listbox"
          tabIndex={-1}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        >
          {loading ? (
            <div className="px-3 py-2 text-sm text-slate-500">Loading…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-500">No options.</div>
          ) : (
            options.map((o) => (
              <button
                key={o.key}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(o);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium text-slate-800 truncate">
                    {o.label}
                  </div>
                  {o.metaRight ? (
                    <div className="text-xs text-slate-600 tabular-nums shrink-0">
                      {o.metaRight}
                    </div>
                  ) : null}
                </div>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

const weekDays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function todayIsoLocal(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function weekdayFromIso(iso: string): string {
  const [y, mo, da] = iso.split("-").map(Number);
  if (!y || !mo || !da) return "";
  return new Date(y, mo - 1, da).toLocaleDateString(undefined, {
    weekday: "long",
  });
}

function formatDisplayDate(iso: string): string {
  const [y, mo, da] = iso.split("-").map(Number);
  if (!y || !mo || !da) return "";
  return new Date(y, mo - 1, da).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function buildHalfHourTimeOptions(): string[] {
  const slots: string[] = [];
  for (let h = 0; h < 24; h += 1) {
    const hh = String(h).padStart(2, "0");
    slots.push(`${hh}:00`);
    slots.push(`${hh}:30`);
  }
  return slots;
}

const HALF_HOUR_OPTIONS = buildHalfHourTimeOptions();

function timeToMinutes(t: string): number {
  const m = String(t).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return NaN;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

export default function DentistAbsentDateTab() {
  const [selectedDate, setSelectedDate] = useState(todayIsoLocal);
  const selectedDay = weekdayFromIso(selectedDate);

  const [rowsByDate, setRowsByDate] = useState<Record<string, AbsentRow[]>>({});
  const rows = rowsByDate[selectedDate] ?? [];
  const [tableLoading, setTableLoading] = useState(false);
  const [deleteLoadingKey, setDeleteLoadingKey] = useState<string | null>(null);

  const refreshForDate = async (iso: string) => {
    setTableLoading(true);
    try {
      const res = await baseUrl.get("/client/setting/dentistAbsentDate/findByDate", {
        params: { date: iso },
      });
      const raw = res.data?.data?.sessions;
      const list: AbsentRow[] = Array.isArray(raw)
        ? raw
            .map((x: unknown) => {
              const o = x as Record<string, unknown>;
              return {
                dentistName: String(o?.dentistName ?? "").trim(),
                day: String(o?.day ?? "").trim(),
                sessionName: String(o?.sessionName ?? "").trim(),
                startTime: String(o?.startTime ?? "").trim(),
                endTime: String(o?.endTime ?? "").trim(),
                roomNo: String(o?.roomNo ?? "").trim(),
                doctorId: String(o?.doctorId ?? "").trim(),
                roomId: String(o?.roomId ?? "").trim(),
              };
            })
            .filter((r: AbsentRow) => r.dentistName && r.day && r.sessionName)
        : [];
      setRowsByDate((prev) => ({ ...prev, [iso]: list }));
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; error?: string } } };
      toast.error(
        ax?.response?.data?.message ||
          ax?.response?.data?.error ||
          "Failed to load absent dates"
      );
      setRowsByDate((prev) => ({ ...prev, [iso]: [] }));
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedDate) return;
    refreshForDate(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  /** When set, modal is editing an existing row (date/dentist locked; save sends oldSessionName). */
  const [editSource, setEditSource] = useState<AbsentRow | null>(null);
  const [modalDate, setModalDate] = useState(todayIsoLocal);
  const [dentists, setDentists] = useState<DentistOption[]>([]);
  const [dentistsLoading, setDentistsLoading] = useState(false);
  const [dentistSessions, setDentistSessions] = useState<DentistGeneralSession[]>(
    []
  );
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const [newRow, setNewRow] = useState({
    dentistName: "",
    doctorId: "",
    day: weekDays[0],
    sessionName: "",
    startTime: "",
    endTime: "",
    roomNo: "",
    roomId: "",
  });

  /** Include current row's session when editing if it is missing from general hours. */
  const dentistSessionsForModal = useMemo(() => {
    const base = dentistSessions;
    const es = editSource;
    if (!es?.sessionName?.trim()) return base;
    const name = es.sessionName.trim();
    if (base.some((s) => s.sessionName === name)) return base;
    return [
      ...base,
      {
        sessionName: name,
        startTime: es.startTime,
        endTime: es.endTime,
        roomNo: es.roomNo,
        roomId: String(es.roomId ?? "").trim(),
      },
    ];
  }, [dentistSessions, editSource]);

  const selectedDentistSession = useMemo(() => {
    const name = String(newRow.sessionName ?? "").trim();
    if (!name) return null;
    return dentistSessionsForModal.find((s) => s.sessionName === name) ?? null;
  }, [dentistSessionsForModal, newRow.sessionName]);

  const startTimeOptions = useMemo(() => {
    const sm = timeToMinutes(selectedDentistSession?.startTime ?? "");
    const em = timeToMinutes(selectedDentistSession?.endTime ?? "");
    if (!Number.isFinite(sm) || !Number.isFinite(em)) return HALF_HOUR_OPTIONS;
    return HALF_HOUR_OPTIONS.filter((t) => {
      const m = timeToMinutes(t);
      return Number.isFinite(m) && m >= sm && m < em;
    });
  }, [selectedDentistSession]);

  const endTimeOptions = useMemo(() => {
    const sm = timeToMinutes(selectedDentistSession?.startTime ?? "");
    const em = timeToMinutes(selectedDentistSession?.endTime ?? "");
    const curStart = timeToMinutes(newRow.startTime ?? "");
    if (!Number.isFinite(sm) || !Number.isFinite(em)) return HALF_HOUR_OPTIONS;
    return HALF_HOUR_OPTIONS.filter((t) => {
      const m = timeToMinutes(t);
      if (!Number.isFinite(m)) return false;
      if (Number.isFinite(curStart) && m <= curStart) return false;
      return m > sm && m <= em;
    });
  }, [selectedDentistSession, newRow.startTime]);

  useEffect(() => {
    if (!isCreateOpen) return;
    const d = weekdayFromIso(modalDate) || weekDays[0];
    setNewRow((p) => (p.day === d ? p : { ...p, day: d }));
  }, [isCreateOpen, modalDate]);

  useEffect(() => {
    if (!isCreateOpen) return;
    let cancelled = false;
    setDentistsLoading(true);
    baseUrl
      .get("/client/setting/dentistAbsentDate/getDentists")
      .then((res) => {
        if (cancelled) return;
        const raw = res.data?.data?.dentists;
        const list: DentistOption[] = Array.isArray(raw)
          ? raw
              .map((x: unknown) => {
                const o = x as Record<string, unknown>;
                return { _id: String(o?._id ?? ""), name: String(o?.name ?? "").trim() };
              })
              .filter((d) => d._id && d.name)
          : [];
        setDentists(list);
        setNewRow((p) =>
          p.doctorId
            ? p
            : {
                ...p,
                dentistName: list[0]?.name ?? "",
                doctorId: list[0]?._id ?? "",
              }
        );
      })
      .catch((err: { response?: { data?: { message?: string; error?: string } } }) => {
        if (cancelled) return;
        setDentists([]);
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to load dentists"
        );
      })
      .finally(() => {
        if (!cancelled) setDentistsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isCreateOpen]);

  useEffect(() => {
    // no room dropdown needed; room comes from selected dentist session
  }, [isCreateOpen]);

  useEffect(() => {
    if (!isCreateOpen) return;
    const iso = modalDate;
    const day = weekdayFromIso(iso) || "Monday";
    const doctorId = String(newRow.doctorId ?? "").trim();
    if (!doctorId) {
      setDentistSessions([]);
      return;
    }
    let cancelled = false;
    setSessionsLoading(true);
    baseUrl
      .get("/client/setting/dentistGeneralHours/list", { params: { day, doctorId } })
      .then((res) => {
        if (cancelled) return;
        const raw = res.data?.data?.sessions;
        const list: DentistGeneralSession[] = Array.isArray(raw)
          ? raw
              .map((x: unknown) => {
                const o = x as Record<string, unknown>;
                return {
                  sessionName: String(o?.sessionName ?? "").trim(),
                  startTime: String(o?.startTime ?? "").trim(),
                  endTime: String(o?.endTime ?? "").trim(),
                  roomNo: String(o?.roomNo ?? "").trim(),
                  roomId: String(o?.roomId ?? "").trim(),
                };
              })
              .filter((s) => s.sessionName)
          : [];
        setDentistSessions(list);
        setNewRow((p) => {
          const first = list[0];
          if (!first)
            return { ...p, sessionName: "", startTime: "", endTime: "" };
          return p.sessionName
            ? p
            : {
                ...p,
                sessionName: first.sessionName,
                startTime: first.startTime,
                endTime: first.endTime,
                roomNo: first.roomNo || p.roomNo,
                roomId: first.roomId || p.roomId,
              };
        });
      })
      .catch((err: { response?: { data?: { message?: string; error?: string } } }) => {
        if (cancelled) return;
        setDentistSessions([]);
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to load dentist sessions"
        );
      })
      .finally(() => {
        if (!cancelled) setSessionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isCreateOpen, modalDate, newRow.doctorId]);

  const openCreate = () => {
    setEditSource(null);
    setModalDate(selectedDate);
    setNewRow((p) => ({
      ...p,
      day: weekdayFromIso(selectedDate) || weekDays[0],
    }));
    setIsCreateOpen(true);
  };

  const openEdit = (r: AbsentRow) => {
    const doctorId = String(r.doctorId ?? "").trim();
    if (!doctorId) {
      toast.error("Cannot edit: missing dentist id.");
      return;
    }
    setEditSource(r);
    setModalDate(selectedDate);
    setNewRow({
      dentistName: r.dentistName,
      doctorId,
      day: r.day || weekdayFromIso(selectedDate) || weekDays[0],
      sessionName: r.sessionName,
      startTime: r.startTime,
      endTime: r.endTime,
      roomNo: r.roomNo,
      roomId: String(r.roomId ?? "").trim(),
    });
    setIsCreateOpen(true);
  };

  const closeCreate = () => {
    setIsCreateOpen(false);
    setEditSource(null);
  };

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalDate) return;
    const payload: Record<string, unknown> = {
      date: modalDate,
      doctorId: newRow.doctorId,
      roomId: newRow.roomId,
      sessionName: newRow.sessionName.trim(),
      startTime: newRow.startTime.trim(),
      endTime: newRow.endTime.trim(),
      active: true,
    };
    if (editSource) {
      payload.oldSessionName = String(editSource.sessionName ?? "").trim();
    }
    if (!payload.doctorId || !payload.roomId || !payload.sessionName) return;
    try {
      await baseUrl.post("/client/setting/dentistAbsentDate/createSession", payload);
      toast.success(editSource ? "Updated" : "Saved");
      setSelectedDate(modalDate);
      await refreshForDate(modalDate);
      closeCreate();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; error?: string } } };
      toast.error(
        ax?.response?.data?.message ||
          ax?.response?.data?.error ||
          "Could not save"
      );
    }
  };

  const onDeleteRow = async (r: AbsentRow) => {
    const doctorId = String(r.doctorId ?? "").trim();
    const roomId = String(r.roomId ?? "").trim();
    const sessionName = String(r.sessionName ?? "").trim();
    if (!doctorId || !roomId || !sessionName) {
      toast.error("Missing identifiers to delete.");
      return;
    }
    const key = `${doctorId}__${roomId}__${selectedDate}__${sessionName}`;
    if (!window.confirm(`Delete ${r.dentistName} — ${sessionName} on ${formatDisplayDate(selectedDate)}?`)) {
      return;
    }
    setDeleteLoadingKey(key);
    try {
      await baseUrl.post("/client/setting/dentistAbsentDate/deleteSession", {
        date: selectedDate,
        doctorId,
        roomId,
        sessionName,
      });
      toast.success("Deleted");
      await refreshForDate(selectedDate);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; error?: string } } };
      toast.error(
        ax?.response?.data?.message ||
          ax?.response?.data?.error ||
          "Could not delete"
      );
    } finally {
      setDeleteLoadingKey(null);
    }
  };

  return (
    <div className="w-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden sm:mx-auto sm:max-w-6xl">
      

      <div className="px-4 pt-3 pb-2 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-1.5">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
            Set by date
          </div>
          <label className="flex flex-col gap-1 sm:inline-flex sm:flex-row sm:items-center sm:gap-2">
            <span className="sr-only">Select date</span>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-1.5 rounded-md text-sm border border-slate-200 bg-white text-slate-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
            />
          </label>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-end">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-800">{selectedDay}</span>
            {selectedDate ? (
              <span className="text-slate-500">
                {" "}
                · {formatDisplayDate(selectedDate)}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition-colors w-full sm:w-auto"
          >
            Create Session
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-center">
          <thead>
            <tr className="bg-slate-50/80">
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[160px]">
                Dentist
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[140px]">
                Day
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[200px]">
                Session Name
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[120px]">
                Start Time
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[120px]">
                End Time
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[120px]">
                Room No
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[120px]">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {tableLoading ? (
              <tr>
                <td className="px-3 py-6 text-sm text-slate-500" colSpan={7}>
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="px-3 py-6 text-sm text-slate-500" colSpan={7}>
                  No absent sessions for this date.
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr
                  key={`${r.doctorId ?? r.dentistName}-${r.roomId ?? r.roomNo}-${r.day}-${r.sessionName}-${idx}`}
                  className="hover:bg-slate-50/50"
                >
                  <td className="px-2 py-2 text-sm font-semibold text-slate-800">
                    {r.dentistName}
                  </td>
                  <td className="px-2 py-2 text-sm font-medium text-slate-700">
                    {r.day || "—"}
                  </td>
                  <td className="px-2 py-2 text-sm font-medium text-slate-700">
                    {r.sessionName}
                  </td>
                  <td className="px-2 py-2 text-sm text-slate-600">
                    {r.startTime}
                  </td>
                  <td className="px-2 py-2 text-sm text-slate-600">
                    {r.endTime}
                  </td>
                  <td className="px-2 py-2 text-sm font-medium text-slate-700">
                    {r.roomNo}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex flex-wrap items-center justify-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => openEdit(r)}
                        className="px-2 py-1 rounded text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => onDeleteRow(r)}
                        disabled={
                          deleteLoadingKey ===
                          `${String(r.doctorId ?? "").trim()}__${String(
                            r.roomId ?? ""
                          ).trim()}__${selectedDate}__${String(
                            r.sessionName ?? ""
                          ).trim()}`
                        }
                        className="px-2 py-1 rounded text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                      >
                        {deleteLoadingKey ===
                        `${String(r.doctorId ?? "").trim()}__${String(
                          r.roomId ?? ""
                        ).trim()}__${selectedDate}__${String(
                          r.sessionName ?? ""
                        ).trim()}`
                          ? "…"
                          : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isCreateOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={closeCreate}
            aria-label="Close modal"
          />
          <div className="relative w-full max-w-sm sm:max-w-md max-h-[90vh] overflow-visible">
            <form
              onSubmit={onSave}
              className="rounded-xl bg-white shadow-lg border border-slate-200 overflow-visible"
            >
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800">
                  {editSource ? "Edit Session" : "Create Session"}
                </div>
                <button
                  type="button"
                  onClick={closeCreate}
                  aria-label="Close"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-4 space-y-3">
                <label className="space-y-1 block">
                  <div className="text-xs font-semibold text-slate-600">Date</div>
                  <input
                    type="date"
                    value={modalDate}
                    onChange={(e) => setModalDate(e.target.value)}
                    disabled={!!editSource}
                    className="w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-slate-800 outline-none focus:border-primary disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-50"
                    required
                  />
                  {modalDate ? (
                    <div className="text-xs text-slate-500 pt-0.5">
                      {weekdayFromIso(modalDate)}
                      <span className="text-slate-400">
                        {" "}
                        · {formatDisplayDate(modalDate)}
                      </span>
                    </div>
                  ) : null}
                </label>

                <label className="space-y-1 block">
                  <div className="text-xs font-semibold text-slate-600">
                    Dentist
                  </div>
                  <div id="absent-dentist-label" className="sr-only">
                    Dentist
                  </div>
                  <SmartDropdown
                    id="absent-dentist"
                    labelId="absent-dentist-label"
                    valueKey={newRow.doctorId}
                    options={dentists.map((d) => ({
                      key: d._id,
                      label: d.name,
                    }))}
                    loading={dentistsLoading}
                    placeholder="Select dentist"
                    required
                    disabled={!!editSource}
                    onSelect={(opt) =>
                      setNewRow((p) => ({
                        ...p,
                        dentistName: opt.label,
                        doctorId: opt.key,
                        sessionName: "",
                        startTime: "",
                        endTime: "",
                        roomNo: "",
                        roomId: "",
                      }))
                    }
                  />
                </label>

                <label className="space-y-1 block">
                  <div className="text-xs font-semibold text-slate-600">Day</div>
                  <div className="w-full px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm font-medium text-slate-700">
                    {weekdayFromIso(modalDate) || newRow.day || "—"}
                  </div>
                </label>

                <label className="space-y-1 block">
                  <div className="text-xs font-semibold text-slate-600">
                    dentist session
                  </div>
                  <div id="absent-session-label" className="sr-only">
                    Dentist session
                  </div>
                  <SmartDropdown
                    id="absent-session"
                    labelId="absent-session-label"
                    valueKey={newRow.sessionName}
                    options={dentistSessionsForModal.map((s) => ({
                      key: s.sessionName,
                      label: s.sessionName,
                      metaRight:
                        s.startTime && s.endTime ? `${s.startTime} · ${s.endTime}` : "",
                    }))}
                    loading={sessionsLoading}
                    placeholder={
                      newRow.doctorId
                        ? "Select dentist session"
                        : "Select dentist first"
                    }
                    required
                    onSelect={(opt) => {
                      const tpl =
                        dentistSessionsForModal.find((s) => s.sessionName === opt.key) ??
                        null;
                      setNewRow((p) => ({
                        ...p,
                        sessionName: opt.key,
                        startTime: tpl?.startTime ?? "",
                        endTime: tpl?.endTime ?? "",
                        roomNo: tpl?.roomNo ?? "",
                        roomId: tpl?.roomId ?? "",
                      }));
                    }}
                  />
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="space-y-1">
                    <div className="text-xs font-semibold text-slate-600">
                      Start Time
                    </div>
                    <div id="absent-start-label" className="sr-only">
                      Start Time
                    </div>
                    <SmartDropdown
                      id="absent-start"
                      labelId="absent-start-label"
                      valueKey={newRow.startTime}
                      options={startTimeOptions.map((t) => ({
                        key: t,
                        label: t,
                      }))}
                      placeholder="Start time"
                      required
                      onSelect={(opt) =>
                        setNewRow((p) => {
                          const nextStart = opt.key;
                          const endM = timeToMinutes(p.endTime);
                          const startM = timeToMinutes(nextStart);
                          return {
                            ...p,
                            startTime: nextStart,
                            endTime:
                              Number.isFinite(endM) &&
                              Number.isFinite(startM) &&
                              endM > startM
                                ? p.endTime
                                : "",
                          };
                        })
                      }
                    />
                  </label>

                  <label className="space-y-1">
                    <div className="text-xs font-semibold text-slate-600">
                      End Time
                    </div>
                    <div id="absent-end-label" className="sr-only">
                      End Time
                    </div>
                    <SmartDropdown
                      id="absent-end"
                      labelId="absent-end-label"
                      valueKey={newRow.endTime}
                      options={endTimeOptions.map((t) => ({
                        key: t,
                        label: t,
                      }))}
                      placeholder="End time"
                      required
                      onSelect={(opt) =>
                        setNewRow((p) => ({ ...p, endTime: opt.key }))
                      }
                    />
                  </label>
                </div>

                <div className="space-y-1 block">
                  <div className="text-xs font-semibold text-slate-600">
                    Room
                  </div>
                  <div className="w-full px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-sm font-medium text-slate-700">
                    {newRow.roomNo ? `Room ${newRow.roomNo}` : "—"}
                  </div>
                  {!newRow.roomId ? (
                    <p className="text-xs text-slate-500">
                      Room is auto-selected from the dentist session.
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeCreate}
                  className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition-colors"
                >
                  {editSource ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
